import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Badge } from "@/components/ui/Badge"
import { formatMoney } from "@/lib/money"
import { plan_index_path, purchase_products_path, push_review_path } from "@/lib/routes"
import { useMarketOrNull } from "@/lib/useMarket"
import ChannelBars from "@/components/home/ChannelBars"
import Delta from "@/components/home/Delta"
import ListCard from "@/components/home/ListCard"
import MonthlyRevenueChart from "@/components/home/MonthlyRevenueChart"
import StatTile from "@/components/home/StatTile"
import TargetBar from "@/components/home/TargetBar"
import { CHANNELS, DATA_UPDATED, MARKET, MARKET_MONTHLY, MOST_EXCESS, RUNNING_OUT } from "./data"

// Wireframe of the per-market Overview (/m/:market_slug). Mock data only; the
// controller passes a real market so the sidebar renders in market mode.
export default function Market() {
  const market = useMarketOrNull()
  const name = market?.name ?? "Vietnam"
  const slug = market?.slug ?? "vietnam"
  const cur = MARKET.currency

  return (
    <AppLayout
      header={
        <div className="flex items-center gap-2">
          <PageTitle title={name} />
          <Badge variant="outline">{cur}</Badge>
        </div>
      }
    >
      <div className="flex max-w-6xl flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label="Revenue this month" value={formatMoney(MARKET.revenueThisMonth, cur)}>
            <Delta pct={MARKET.changePct} /> vs last month
          </StatTile>
          <StatTile label="Revenue this year" value={formatMoney(MARKET.revenueThisYear, cur)}>
            <TargetBar actual={MARKET.revenueThisYear} target={MARKET.yearTarget} currency={cur} />
          </StatTile>
          <StatTile label="Products running out" value={MARKET.runningOut} tone="warning">
            Stock will not last until the next delivery
          </StatTile>
          <StatTile label="Products with excess stock" value={MARKET.excess.total}>
            {MARKET.excess.overstock} overstock · {MARKET.excess.slow} slow · {MARKET.excess.dead} dead
          </StatTile>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <MonthlyRevenueChart
              title="Revenue by month"
              subtitle="Actual revenue against the plan"
              currency={cur}
              data={MARKET_MONTHLY}
              bars={[{ key: "actual", name: "Actual", color: "var(--series-1)" }]}
              line={{ key: "plan", name: "Plan" }}
            />
          </div>
          <ChannelBars channels={CHANNELS} currency={cur} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <ListCard
            title="Running out soonest"
            rows={RUNNING_OUT.map((p) => ({
              label: p.name,
              value: <span className={p.urgent ? "font-medium text-warning" : undefined}>{p.note}</span>,
            }))}
            footer={{ label: `All ${MARKET.runningOut} in Purchase Plan`, href: purchase_products_path() }}
          />
          <ListCard
            title="Most excess stock"
            rows={MOST_EXCESS.map((p) => ({ label: p.name, value: p.note }))}
            footer={{ label: `All ${MARKET.excess.total} in Push`, href: push_review_path(slug) }}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <ListCard
            title="Planning"
            rows={[
              { label: "Top Down plan 2026", value: <Badge variant="success">Published · version 2</Badge> },
              { label: "Revision", value: <Badge variant="warning">Waiting for review</Badge> },
              { label: "Bottom Up by month", value: "9 of 12 months filled" },
            ]}
            footer={{ label: "Open Planning", href: plan_index_path(slug) }}
          />
          <ListCard
            title="Data last updated"
            rows={DATA_UPDATED.map((d) => ({
              label: d.label,
              value: <span className={d.stale ? "font-medium text-warning" : undefined}>{d.note}</span>,
            }))}
          />
        </div>
      </div>
    </AppLayout>
  )
}
